import React, { forwardRef } from 'react';
import styled from 'styled-components';

interface TextFieldProps {
  label?: string;
  placeholder?: string;
  value?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  type?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  multiline?: boolean;
  rows?: number;
  required?: boolean;
  name?: string;
  id?: string;
}

const InputContainer = styled.div<{ $fullWidth: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: ${props => props.$fullWidth ? '100%' : 'auto'};
`;

const Label = styled.label<{ $error: boolean }>`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.$error ? props.theme.destructive : props.theme.foreground};
`;

const StyledInput = styled.input<{
  $error: boolean;
  $disabled: boolean;
  $size: string;
  $fullWidth: boolean;
}>`
  width: ${props => props.$fullWidth ? '100%' : 'auto'};
  padding: ${props => {
    switch (props.$size) {
      case 'sm': return '0.375rem 0.75rem';
      case 'lg': return '0.75rem 1rem';
      default: return '0.5rem 0.875rem';
    }
  }};
  font-size: ${props => {
    switch (props.$size) {
      case 'sm': return '0.875rem';
      case 'lg': return '1rem';
      default: return '0.875rem';
    }
  }};
  background: ${props => props.theme.input || props.theme.background};
  border: 1px solid ${props => props.$error ? props.theme.destructive : props.theme.border};
  border-radius: ${props => props.theme.radius};
  color: ${props => props.theme.foreground};
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    border-color: ${props => props.theme.ring || props.theme.primary};
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.ring || props.theme.primary};
    box-shadow: 0 0 0 2px ${props => (props.theme.ring || props.theme.primary) + '40'};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  &::placeholder {
    color: var(--muted-foreground);
  }
`;

const StyledTextarea = styled.textarea<{
  $error: boolean;
  $disabled: boolean;
  $size: string;
  $fullWidth: boolean;
}>`
  width: ${props => props.$fullWidth ? '100%' : 'auto'};
  padding: ${props => {
    switch (props.$size) {
      case 'sm': return '0.375rem 0.75rem';
      case 'lg': return '0.75rem 1rem';
      default: return '0.5rem 0.875rem';
    }
  }};
  font-size: ${props => {
    switch (props.$size) {
      case 'sm': return '0.875rem';
      case 'lg': return '1rem';
      default: return '0.875rem';
    }
  }};
  background: ${props => props.theme.input || props.theme.background};
  border: 1px solid ${props => props.$error ? props.theme.destructive : props.theme.border};
  border-radius: ${props => props.theme.radius};
  color: ${props => props.theme.foreground};
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 80px;
  
  &:hover:not(:disabled) {
    border-color: ${props => props.theme.ring || props.theme.primary};
  }
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.ring || props.theme.primary};
    box-shadow: 0 0 0 2px ${props => (props.theme.ring || props.theme.primary) + '40'};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  &::placeholder {
    color: ${props => props.theme.mutedForeground};
  }
`;

const HelperText = styled.span<{ $error: boolean }>`
  font-size: 0.75rem;
  color: ${props => props.$error ? props.theme.destructive : props.theme.mutedForeground};
`;

const TextField = forwardRef<HTMLInputElement | HTMLTextAreaElement, TextFieldProps>(
  ({
    label,
    placeholder,
    value,
    onChange,
    onFocus,
    onBlur,
    type = 'text',
    disabled = false,
    error = false,
    helperText,
    size = 'md',
    fullWidth = false,
    multiline = false,
    rows = 3,
    required = false,
    name,
    id
  }, ref) => {
    const commonProps = {
      ref: ref as any,
      placeholder,
      value,
      onChange,
      onFocus,
      onBlur,
      disabled,
      $error: error,
      $disabled: disabled,
      $size: size,
      $fullWidth: fullWidth,
      name,
      id
    };

    return (
      <InputContainer $fullWidth={fullWidth}>
        {label && (
          <Label $error={error}>
            {label}
            {required && <span style={{ color: 'var(--destructive)' }}> *</span>}
          </Label>
        )}
        
        {multiline ? (
          <StyledTextarea
            {...commonProps}
            rows={rows}
          />
        ) : (
          <StyledInput
            {...commonProps}
            type={type}
          />
        )}
        
        {helperText && (
          <HelperText $error={error}>
            {helperText}
          </HelperText>
        )}
      </InputContainer>
    );
  }
);

TextField.displayName = 'TextField';

export default TextField; 